import { useCallback, useState, useEffect, useReducer } from 'react';
import { useDebouncedCallback } from 'use-debounce';

export function createTimeoutState(setState) {
    let timer = 0;
    let buff = {};
    let ends = [];
    let counter = 0;
    const fireEnds = () => {
        const toEnds = ends.concat();
        ends = [];
        toEnds.forEach(end => end());
    };
    return (data, end = null) => {
        counter += 1;
        buff = { ...buff, ...data };
        if (end) {
            ends.push(end);
        }
        clearTimeout(timer);
        timer = setTimeout(() => {
            console.log(`Flush state[${counter}]:`, buff);
            setState(buff, fireEnds);
            counter = 0;
        });
    };
}

export function createHookTimeoutState(initState) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [state, setState] = useState(initState);
    return [state, createTimeoutState(data => setState({ ...state, ...data }))];
}

export const useCounter = (
    init = 0,
    { lower = -Infinity, upper = Infinity, step = 1 } = {}
) => {
    const [value, setValue] = useState(init);
    return {
        value,
        setValue,
        increase: useCallback(() => {
            setValue(value => {
                const nextValue = value + step;
                return nextValue <= upper ? nextValue : value;
            });
        }),
        decrease: useCallback(() => {
            setValue(value => {
                const prevValue = value - step;
                return prevValue >= lower ? prevValue : value;
            });
        }),
        reset: useCallback(() => {
            setValue(init);
        }),
    };
};

export const useBoolean = init => {
    const [value, setValue] = useState(init);
    return {
        value,
        setValue,
        toggle: useCallback(() => setValue(v => !v), []),
        enable: useCallback(() => setValue(true), []),
        disable: useCallback(() => setValue(false), []),
        lock: promise => {
            if (!value) {
                setValue(true);
                promise.finally(() => setValue(false));
            }
            return promise;
        },
    };
};

export const useArray = (init = []) => {
    const [value, setValue] = useState(init);
    return {
        value,
        setValue,
        isEmpty: useCallback(() => value.length === 0),
        append: useCallback(item => setValue(v => v.concat([item])), []),
        extend: useCallback(items => {
            if (Array.isArray(items)) {
                setValue(v => v.concat(items));
            } else {
                console.warn(`Array expteted. Got: ${typeof items}`);
            }
        }),
        reset: useCallback(() => setValue(() => []), []),
        remove: useCallback(item => setValue(v => v.filter(v => v === item))),
    };
};

export const useOnMount = onMount =>
    useEffect(() => {
        const result = onMount();
        if (typeof result === 'function') {
            return result;
        }
    }, []);

export const useOnUnmount = onUnmount => useEffect(() => () => onUnmount(), []);

export const useActionReducer = (reducer, initState) => {
    const [state, dispatch] = useReducer(reducer, initState);
    const actionDispatch = (action, payload = {}) =>
        dispatch({
            action,
            payload,
        });
    return [state, actionDispatch];
};

export const useSelectDebounce = loadData => {
    const [callback, setCallback] = useState();

    const debouncedHandleSearch = useDebouncedCallback(async term => {
        if (callback && term) {
            const result = await loadData(term);
            callback(result);
        }
    }, 500);

    return { setCallback, setSearchTerm: debouncedHandleSearch };
};
